import {
  ActionIcon,
  AspectRatio,
  Badge,
  Button,
  Center,
  Grid,
  GridProps,
  Group,
  Image,
  Menu,
  Modal,
  Overlay,
  Pill,
  rem,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import { useDisclosure, useHover } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import {
  IconDots,
  IconEdit,
  IconPercentage,
  IconPlus,
  IconShoppingCart,
  IconTrash,
  IconZoom,
} from '@tabler/icons-react'
import LoadingImage from 'common/LoadingImage'
import NoImage from 'common/NoImage'
import { useIsAdmin } from 'hooks/auth'
import { useDeleteProduct } from 'hooks/products'
import { useEffect, useState } from 'react'
import { Category, Product } from 'types/products'
import { getCategoryName, getMiniImageUrl, validateImageUrl } from 'utils'
import styles from './ProductDetailCard.module.scss'

interface Props extends GridProps {
  product: Product
}

export default function ProductDetailCard({ product, ...gridProps }: Props) {
  const { id, image, symbol, alternateSymbol, manufacturer, isAtSale, availableSizes } = product
  const [opened, { open, close }] = useDisclosure(false)
  const [visible, setVisible] = useState(false)
  const { hovered, ref } = useHover()
  const deleteProduct = useDeleteProduct()
  const isAdmin = useIsAdmin()

  function openEditProductModal() {
    modals.openContextModal({
      modal: 'editProduct',
      centered: true,
      title: `Edytuj produkt ${symbol}`,
      innerProps: {
        productId: id,
        initialValues: {
          symbol,
          alternateSymbol,
          image,
          isAtSale,
          manufacturerId: manufacturer?.id!,
          availableSizes,
        },
      },
    })
  }

  function openAddVariantModal(category: Category) {
    modals.openContextModal({
      modal: 'addVariant',
      centered: true,
      title: `Dodaj ${getCategoryName(category).biernik}`,
      innerProps: {
        productId: id,
        category,
      },
    })
  }

  function openDeleteModal() {
    modals.openConfirmModal({
      title: 'Usuń produkt',
      centered: true,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            Czy na pewno chcesz usunąć wszystkie produkty (metrażowe, gotowe, itd.) o symbolu <b>{symbol}</b>? Nie
            będzie można cofnąć tej operacji.
          </Text>
          <Text size="sm">
            Usunięte zostaną równie wszystkie powiązane z tym produktem warianty, braki oraz zamówienia.
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Usuń produkt', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteProduct.mutateAsync(product),
    })
  }

  useEffect(() => {
    setVisible(hovered)
  }, [hovered])

  const [isImageLinkValid, setIsImageLinkValid] = useState<boolean>(false)
  const [imageUrl, setImageUrl] = useState<string | undefined>()

  useEffect(() => {
    if (image) {
      getMiniImageUrl(image).then(setImageUrl)
    }

    validateImageUrl(image).then(setIsImageLinkValid)
  }, [image])

  return (
    <>
      <Modal opened={opened} onClose={close} centered withCloseButton={false} size="auto" padding={0}>
        <Image src={image} alt={symbol} style={{ width: 'auto', height: '800px' }} />
      </Modal>
      <Grid {...gridProps}>
        <Grid.Col span={{ base: 12, md: 6, lg: 5, xl: 4 }}>
          {image ? (
            imageUrl ? (
              <AspectRatio className={styles.imageWrapper} ratio={1} ref={ref}>
                <Image
                  className={isImageLinkValid ? styles.image : undefined}
                  onClick={isImageLinkValid ? open : undefined}
                  src={imageUrl}
                  alt={symbol}
                  fallbackSrc="https://placehold.co/250x250/dee2e6/ffffff?text=Niepoprawny+link&font=roboto"
                />
                {isImageLinkValid && visible && (
                  <Overlay className={styles.overlay} blur={2} backgroundOpacity={0.25}>
                    <Center h="100%">
                      <IconZoom color="white" size={130} />
                    </Center>
                  </Overlay>
                )}
              </AspectRatio>
            ) : (
              <LoadingImage />
            )
          ) : (
            <NoImage />
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 7, xl: 8 }}>
          <Stack mt="md" ml="xs" gap={0}>
            <Group justify="space-between">
              <Text truncate="end" fw={700} size="2.125rem">
                {symbol}
              </Text>
              <Group>
                <Menu
                  offset={2}
                  width={152}
                  transitionProps={{ transition: 'fade-down' }}
                  position="bottom-end"
                  shadow="md"
                >
                  <Menu.Target>
                    <Button variant="light" leftSection={<IconPlus size={18} />}>
                      Dodaj produkt
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {Object.values(Category).map((category) => (
                      <Menu.Item
                        key={category}
                        onClick={() => openAddVariantModal(category)}
                        style={{ textAlign: 'center' }}
                      >
                        {getCategoryName(category, { short: true, capitalize: true }).mianownik}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>

                <Menu withArrow transitionProps={{ transition: 'fade-down' }} position="bottom-end" shadow="md">
                  <Menu.Target>
                    <ActionIcon size="lg" variant="default">
                      <IconDots size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconShoppingCart size={16} stroke={1.5} />}
                      onClick={() => window.open(`https://abcfirany.pl/szukaj.html/szukaj=${symbol}`)}
                    >
                      Znajdź na sklepie
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconEdit size={16} stroke={1.5} />}
                      disabled={!isAdmin}
                      onClick={openEditProductModal}
                    >
                      Edytuj
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconTrash size={16} stroke={1.5} />}
                      color="red"
                      disabled={!isAdmin}
                      onClick={openDeleteModal}
                    >
                      Usuń
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
            {isAtSale && (
              <Badge
                mt="sm"
                size="lg"
                color="red"
                variant="light"
                leftSection={<IconPercentage stroke={3} style={{ width: rem(18), height: rem(18) }} />}
              >
                Wyprzedaż
              </Badge>
            )}
            <Table variant="vertical" withTableBorder mt="md">
              <Table.Tbody>
                {alternateSymbol && (
                  <Table.Tr>
                    <Table.Th w={200}>Symbol alternatywny</Table.Th>
                    <Table.Td>{alternateSymbol}</Table.Td>
                  </Table.Tr>
                )}
                {manufacturer && (
                  <Table.Tr>
                    <Table.Th w={200}>Producent</Table.Th>
                    <Table.Td>{manufacturer.name}</Table.Td>
                  </Table.Tr>
                )}
                {availableSizes && availableSizes.length > 0 && (
                  <Table.Tr>
                    <Table.Th w={200}>Dostępne wymiary</Table.Th>
                    <Table.Td>
                      <Group gap="xs" wrap="wrap">
                        {availableSizes.map((size) => (
                          <Pill key={size}>{size}</Pill>
                        ))}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Stack>
        </Grid.Col>
      </Grid>
    </>
  )
}
